import React, { useEffect, useState } from 'react';
import { Container, Button } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import IconWrapper from './IconWrapper';
import { useGallery } from '../hooks/useGallery';
import './Christmas.css';
import './Gallery.css'; // <- kvůli reuse lightbox stylů z galerie

const REENIO_SCRIPT_ID = 'reenio-widget-script';

type LightboxItem = {
  src: string;
  alt: string;
  title: string;
};

const Christmas: React.FC = () => {
  // --- vezmeme data z galerie, jen kvůli kategorii "christmas"
  const { images, getImageUrl, getPlaceholderUrl } = useGallery();

  // --- lightbox stav
  const [items, setItems] = useState<LightboxItem[]>([]);
  const [index, setIndex] = useState<number | null>(null);

  // otevřít lightbox se snímky z kategorie "christmas"
  const openXmasLightbox = () => {
    const christmas = images.find((i) => i.category === 'christmas');
    if (!christmas) return;

    const fns = Array.isArray(christmas.filename)
      ? christmas.filename
      : [christmas.filename];

    const list: LightboxItem[] = fns.map((f) => ({
      src: getImageUrl(f),
      alt: (christmas as any).alt ?? christmas.title ?? 'Vánoční fotografie',
      title: christmas.title ?? 'Vánoční fotografie',
    }));

    setItems(list);
    setIndex(0);
  };

  const closeLightbox = () => {
    setIndex(null);
    setItems([]);
  };
  const goNext = () => {
    if (index == null || items.length === 0) return;
    setIndex((index + 1) % items.length);
  };
  const goPrev = () => {
    if (index == null || items.length === 0) return;
    setIndex((index - 1 + items.length) % items.length);
  };

  // klávesové zkratky (Esc / ← / →)
  useEffect(() => {
    if (index == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, items.length]);

  // --- Reenio skript (beze změn stylu)
  useEffect(() => {
    const host = document.getElementById('reenio-slot');
    if (!host) return;

    host.querySelectorAll('.reenio-iframe').forEach((n) => n.remove());

    const div = document.createElement('div');
    div.className = 'reenio-iframe';
    div.setAttribute('data-size', 'auto');
    host.appendChild(div);

    if (!document.getElementById(REENIO_SCRIPT_ID)) {
      const s = document.createElement('script');
      s.id = REENIO_SCRIPT_ID;
      s.src = 'https://reenio.cz/cs/GU2DCOA/widget-iframe.js';
      s.async = true;
      s.defer = true;
      document.body.appendChild(s);
    }
    return () => { };
  }, []);

  return (
    <div className="christmas-page">
      <div className="christmas-hero">
        <Container className="text-center">
          <motion.h1
            className="christmas-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Vánoční focení
          </motion.h1>

          <motion.p
            className="christmas-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            Rezervujte si svůj termín pomocí formuláře níže.
          </motion.p>

          {/* Tlačítko ve stejném stylu jako zbytek – Bootstrap outline-light */}
          <motion.div
            className="mt-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Button variant="outline-light" size="lg" onClick={openXmasLightbox}>
              Podívejte se na vánoční fotky
            </Button>
          </motion.div>
        </Container>
      </div>

      <Container className="py-5">
        <div id="reenio-slot" className="reenio-slot" />
      </Container>

      {/* LIGHTBOX – používá stejné třídy jako galerie => dědí stejný vzhled */}
      <AnimatePresence>
        {index != null && items.length > 0 && (
          <motion.div
            className="lightbox-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
          >
            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="lightbox-close"
                onClick={closeLightbox}
                aria-label="Zavřít"
              >
                <IconWrapper icon={FaTimes} />
              </button>

              <button
                type="button"
                className="lightbox-arrow left"
                onClick={goPrev}
                aria-label="Předchozí"
              >
                <IconWrapper icon={FaChevronLeft} />
              </button>

              <img
                className="lightbox-image"
                src={items[index].src}
                alt={items[index].alt}
                onError={(e) => {
                  const target = e.currentTarget as HTMLImageElement;
                  target.src = getPlaceholderUrl();
                }}
              />

              <button
                type="button"
                className="lightbox-arrow right"
                onClick={goNext}
                aria-label="Další"
              >
                <IconWrapper icon={FaChevronRight} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Christmas;